import * as Types from '../../generated.types'

import {
  OrganizationDetailFragmentDoc,
  AliceCardProgramFragmentDoc,
  AliceCardFundingFragmentDoc,
  PaymentMethodConnectionFragmentDoc,
  PaymentMethodFragmentDoc,
  BillingSourceFragmentDoc,
  EmployerSummaryFragmentDoc,
  OnboardingWorkflowFragmentDoc,
} from '../../fragments/OrganizationFragment_gen'
import { TelephoneFragmentDoc } from '../../fragments/TelephoneFragment_gen'
import { FinchConnectionDetailFragmentDoc } from '../../fragments/FinchConnectionFragment_gen'
import { PaymentCardFragmentDoc } from '../../fragments/PaymentCardFragment_gen'
import { ErrorFragmentDoc } from '../../fragments/ErrorFragment_gen'
import { BankingAccountFragmentDoc } from '../../fragments/BankingAccountFragment_gen'
import { WorkLocationFragmentDoc } from '../../fragments/WorkLocationFragment_gen'
import { AddressFragmentDoc } from '../../fragments/AddressFragment_gen'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { fetchGql } from '@alice-financial/api'
export type OrgDashboardQueryVariables = Types.Exact<{
  orgId?: Types.InputMaybe<Types.Scalars['Int']>
}>

export type OrgDashboardQuery = {
  __typename?: 'Query'
  organization?: {
    __typename: 'Organization'
    id: number
    name?: string | null
    legalName?: string | null
    contractSignedOn?: string | null
    launchDate?: string | null
    isOnboarded?: boolean | null
    ein?: string | null
    tel?: string | null
    websiteUrl?: string | null
    businessStructure?: Types.OrganizationBusinessStructure | null
    mcc?: { __typename?: 'MCC'; mcc: number; label: string } | null
    address?: {
      __typename?: 'AddressDisplay'
      line1?: string | null
      line2?: string | null
      cityName?: string | null
      stateCode?: string | null
      zipcode?: string | null
      isVerified: boolean
      verificationStatus?: Types.AddressVerificationStatus | null
    } | null
    logo?: { __typename?: 'Image'; url?: string | null; thumbnailUrl?: string | null } | null
    aliceCardProgram: {
      __typename?: 'AliceCardProgram'
      kycStatus: Types.AliceCardKycStatus
      hasFundingConnected: boolean
      cardIssuingStatus: Types.AliceCardIssuingStatus
      hasUnifiedBanking?: boolean | null
      employeeFundingCount: number
      perEmployeeFundingAmount: number
      reserveAmount: number
      companyAccount?: {
        __typename?: 'StripeCompanyAccount'
        id: number
        accountStatus: Types.StripeCompanyAccountStatusEnum
        stripeConnectedAccountId?: string | null
        stripeFinancialAccountId?: string | null
        requirementsPendingVerification: Array<string>
        currentlyDueRequirements: Array<string>
        companyRepresentative?: {
          __typename?: 'User'
          id: number
          firstName?: string | null
          lastName?: string | null
          email?: string | null
          birthdate?: string | null
          ssnLast4?: string | null
          tel?: {
            __typename?: 'Telephone'
            nationalNumber: string
            isSmsConfirmed?: boolean | null
            isConfirmationCodeSent?: boolean | null
          } | null
          address?: {
            __typename?: 'AddressDisplay'
            line1?: string | null
            line2?: string | null
            cityName?: string | null
            stateCode?: string | null
            zipcode?: string | null
          } | null
        } | null
      } | null
    }
    aliceCardFunding: {
      __typename?: 'AliceCardFunding'
      status: Types.AliceCardFundingStatus
      paymentMethodConnections?: Array<{
        __typename?: 'PaymentMethodConnection'
        id?: number | null
        isCurrent: boolean
        isActive: boolean
        paymentMethod: {
          __typename?: 'PaymentMethod'
          id?: number | null
          last4?: string | null
          descriptor: string
          status: Types.PaymentMethodStatus
          stripeSystemId?: string | null
        }
      }> | null
      availablePaymentMethods?: Array<{
        __typename?: 'PaymentMethod'
        id?: number | null
        last4?: string | null
        descriptor: string
        status: Types.PaymentMethodStatus
        stripeSystemId?: string | null
      }> | null
    }
    payrollConnection: {
      __typename?: 'PayrollConnection'
      payrollInstructionsCompletedAt?: string | null
      payrollConnectedAt?: string | null
      isFinchConnectEnabled: boolean
      finchConnections: Array<{
        __typename: 'FinchConnection'
        id: number
        sessionId?: string | null
        displayLabel?: string | null
        isActive: boolean
        status?: Types.FinchConnectionStatus | null
        hasAuthenticatedEver: boolean
      }>
      payrollPlatform?: {
        __typename?: 'PayrollPlatform'
        id: number
        name: string
        code: string
        permitsAdminReports: boolean
      } | null
    }
    defaultPayrollPlatform?: { __typename?: 'PayrollPlatform'; id: number; name: string; code: string } | null
    billing?: {
      __typename?: 'Billing'
      hasUnifiedBilling: boolean
      hasBillingConnected: boolean
      billingWorkspace: Types.BillingWorkspace
      billingSource?:
        | {
            __typename: 'BankingAccount'
            id: number
            billingSystemId?: string | null
            name?: string | null
            cardType?: Types.BankCardType | null
            accountType?: Types.BankingAccountType | null
            platform: Types.BankingAccountPlatform
            accountLast4?: string | null
            error?: { __typename?: 'Error'; message?: string | null; code?: string | null } | null
          }
        | {
            __typename: 'PaymentCard'
            id: number
            billingSystemId?: string | null
            brand: string
            expMonth: number
            expYear: number
            last4: string
            error?: { __typename?: 'Error'; message?: string | null; code?: string | null } | null
          }
        | null
    } | null
    employers: Array<{
      __typename: 'Employer'
      id: number
      name?: string | null
      marketingFriendlyName?: string | null
      inviteCode: string
      finchConnection?: {
        __typename: 'FinchConnection'
        id: number
        sessionId?: string | null
        displayLabel?: string | null
        isActive: boolean
        status?: Types.FinchConnectionStatus | null
        hasAuthenticatedEver: boolean
      } | null
      aliceCardFunding: {
        __typename?: 'AliceCardFunding'
        status: Types.AliceCardFundingStatus
        paymentMethodConnections?: Array<{
          __typename?: 'PaymentMethodConnection'
          id?: number | null
          isCurrent: boolean
          isActive: boolean
          paymentMethod: {
            __typename?: 'PaymentMethod'
            id?: number | null
            last4?: string | null
            descriptor: string
            status: Types.PaymentMethodStatus
            stripeSystemId?: string | null
          }
        }> | null
        availablePaymentMethods?: Array<{
          __typename?: 'PaymentMethod'
          id?: number | null
          last4?: string | null
          descriptor: string
          status: Types.PaymentMethodStatus
          stripeSystemId?: string | null
        }> | null
      }
      billing?: {
        __typename?: 'Billing'
        hasUnifiedBilling: boolean
        hasBillingConnected: boolean
        billingWorkspace: Types.BillingWorkspace
        billingSource?:
          | {
              __typename: 'BankingAccount'
              id: number
              billingSystemId?: string | null
              name?: string | null
              cardType?: Types.BankCardType | null
              accountType?: Types.BankingAccountType | null
              platform: Types.BankingAccountPlatform
              accountLast4?: string | null
              error?: { __typename?: 'Error'; message?: string | null; code?: string | null } | null
            }
          | {
              __typename: 'PaymentCard'
              id: number
              billingSystemId?: string | null
              brand: string
              expMonth: number
              expYear: number
              last4: string
              error?: { __typename?: 'Error'; message?: string | null; code?: string | null } | null
            }
          | null
      } | null
      employees: {
        __typename?: 'EmployeeConnection'
        reachableCount: number
        totalCount: number
        enrolledCount: number
        totalDeductions: number
        totalSavings: number
      }
    } | null>
    workLocations: Array<{
      __typename?: 'WorkLocation'
      id: number
      name: string
      latitude?: number | null
      longitude?: number | null
      logo?: { __typename?: 'Image'; url?: string | null; thumbnailUrl?: string | null } | null
      enrollmentMaterials?: { __typename?: 'EnrollmentMaterials'; zippedMaterialsUrl?: string | null } | null
      employersInfo?: Array<{ __typename?: 'Employer'; id: number; name?: string | null }> | null
      address?: {
        __typename?: 'AddressDisplay'
        isVerified: boolean
        isMailing: boolean
        line1?: string | null
        line2?: string | null
        cityName?: string | null
        stateCode?: string | null
        zipcode?: string | null
      } | null
      preshiftEvents: Array<{
        __typename?: 'PreshiftEvent'
        id: number
        scheduledAt?: string | null
        inviteeName?: string | null
        inviteeEmail?: string | null
        createdAt: string
      }>
    } | null>
    employees: {
      __typename?: 'EmployeeConnection'
      reachableCount: number
      totalCount: number
      enrolledCount: number
      totalDeductions: number
      totalSavings: number
    }
    onboardingWorkflow?: {
      __typename?: 'OnboardingWorkflow'
      id: number
      status: Types.OnboardingStatus
      activityLog: Array<{
        __typename?: 'OnboardingLogMessage'
        what: string
        when: string
        stepType: Types.OnboardingStepType
        resourceUrl?: string | null
        who?: { __typename?: 'OnboardingLogUser'; id: number; name: string; email: string } | null
      }>
      steps: Array<{
        __typename?: 'OnboardingStep'
        id: number
        stepType: Types.OnboardingStepType
        status: Types.OnboardingStatus
        assignee: {
          __typename?: 'User'
          id: number
          firstName?: string | null
          lastName?: string | null
          email?: string | null
        }
        tasks: Array<{
          __typename?: 'OnboardingStepTask'
          id: number
          taskType: string
          status: Types.OnboardingStatus
        }>
      }>
    } | null
    reports: {
      __typename?: 'OrganizationReports'
      aliceCardReconciliation: {
        __typename?: 'OrganizationReport'
        id: string
        description: string
        downloadUrl: string
        title: string
      }
      financial: {
        __typename?: 'OrganizationReport'
        id: string
        description: string
        downloadUrl: string
        title: string
      }
      invoices: {
        __typename?: 'OrganizationReport'
        id: string
        description: string
        downloadUrl: string
        title: string
      }
      payPeriods: {
        __typename?: 'OrganizationReport'
        id: string
        description: string
        downloadUrl: string
        title: string
      }
    }
  } | null
}

export const OrgDashboardDocument = `
    query OrgDashboard($orgId: Int) {
  organization(id: $orgId) {
    ...OrganizationDetail
  }
}
    ${OrganizationDetailFragmentDoc}
${AliceCardProgramFragmentDoc}
${TelephoneFragmentDoc}
${AliceCardFundingFragmentDoc}
${PaymentMethodConnectionFragmentDoc}
${PaymentMethodFragmentDoc}
${FinchConnectionDetailFragmentDoc}
${BillingSourceFragmentDoc}
${PaymentCardFragmentDoc}
${ErrorFragmentDoc}
${BankingAccountFragmentDoc}
${EmployerSummaryFragmentDoc}
${WorkLocationFragmentDoc}
${AddressFragmentDoc}
${OnboardingWorkflowFragmentDoc}`
export const useOrgDashboardQuery = <TData = OrgDashboardQuery, TError = Error>(
  variables?: OrgDashboardQueryVariables,
  options?: UseQueryOptions<OrgDashboardQuery, TError, TData>
) =>
  useQuery<OrgDashboardQuery, TError, TData>(
    variables === undefined ? ['OrgDashboard'] : ['OrgDashboard', variables],
    fetchGql<OrgDashboardQuery, OrgDashboardQueryVariables>(OrgDashboardDocument, variables),
    options
  )

useOrgDashboardQuery.getKey = (variables?: OrgDashboardQueryVariables) =>
  variables === undefined ? ['OrgDashboard'] : ['OrgDashboard', variables]
